import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/common/DatePicker";

/**
 * Two-mode date filter:
 *   - "month" → pick a YYYY-MM from a list of known months
 *   - "range" → pick a from/to date pair
 * `value` shape: { mode: "month"|"range", month?: string, from?: string, to?: string }
 */
export default function DateRangeControl({ value, onChange, months, testIdPrefix = "dash" }) {
  const setMode = (m) => onChange({ ...value, mode: m });
  const setMonth = (v) => onChange({ ...value, mode: "month", month: v });
  const setFrom = (v) => onChange({ ...value, mode: "range", from: v });
  const setTo = (v) => onChange({ ...value, mode: "range", to: v });

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-date-control`}>
      <div className="glass-solid p-0.5 rounded-lg flex text-xs">
        <button
          onClick={() => setMode("month")}
          className={`px-3 py-1 rounded-md transition-colors ${
            value.mode === "month" ? "bg-white shadow font-medium text-slate-900" : "text-slate-500"
          }`}
          data-testid={`${testIdPrefix}-mode-month`}
        >
          Month
        </button>
        <button
          onClick={() => setMode("range")}
          className={`px-3 py-1 rounded-md transition-colors ${
            value.mode === "range" ? "bg-white shadow font-medium text-slate-900" : "text-slate-500"
          }`}
          data-testid={`${testIdPrefix}-mode-range`}
        >
          Range
        </button>
      </div>

      {value.mode === "month" ? (
        <Select value={value.month || "all"} onValueChange={setMonth}>
          <SelectTrigger className="w-[170px] glass-input" data-testid={`${testIdPrefix}-month-select`}>
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {(months || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div className="flex gap-1 items-center">
          <DatePicker
            value={value.from || ""}
            onChange={setFrom}
            placeholder="From"
            className="w-[160px]"
            testId={`${testIdPrefix}-date-from`}
          />
          <span className="text-slate-400">→</span>
          <DatePicker
            value={value.to || ""}
            onChange={setTo}
            placeholder="To"
            className="w-[160px]"
            testId={`${testIdPrefix}-date-to`}
          />
        </div>
      )}
    </div>
  );
}

/** Extract API params from a DateRangeControl `value`. Returns e.g.
 *  { month: "2026-06" } OR { date_from, date_to } OR {} */
export function dateParamsFrom(value) {
  if (!value) return {};
  if (value.mode === "range") {
    const p = {};
    if (value.from) p.date_from = value.from;
    if (value.to) p.date_to = value.to;
    return p;
  }
  if (value.month && value.month !== "all") return { month: value.month };
  return {};
}
